const API_BASE_URL = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:3005/api/v1';

// ============== Types ==============

export interface SubscriptionTier {
  id: number;
  tier_name: string;
  tier_display_name: string;
  features: Record<string, boolean>;
  limits: Record<string, number>;
  pricing: {
    monthly_price: number;
    annual_price: number;
    currency: string;
  };
  trial_days: number;
  grace_period_days: number;
  stripe_monthly_price_id?: string;
  stripe_annual_price_id?: string;
  is_active: boolean;
}

export interface Subscription {
  id: string;
  organization_id: string;
  stripe_subscription_id: string;
  stripe_customer_id: string;
  subscription_tier_id: number;
  billing_cycle: 'monthly' | 'annual';
  base_amount: number;
  discount_amount: number;
  total_amount: number;
  currency: string;
  status: 'active' | 'trialing' | 'past_due' | 'canceled' | 'suspended';
  trial_start: string | null;
  trial_end: string | null;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  canceled_at: string | null;
  tier_name: string;
  tier_display_name: string;
}

export interface UpgradePreview {
  proratedAmount: number;
  newPrice: number;
  newTier: string;
  billingCycle: string;
  nextBillingDate: string;
}

// Raw API response may use snake_case
interface UpgradePreviewRaw {
  prorated_amount?: number;
  proratedAmount?: number;
  new_price?: number;
  newPrice?: number;
  new_tier?: string;
  newTier?: string;
  billing_cycle?: string;
  billingCycle?: string;
  next_billing_date?: string;
  nextBillingDate?: string;
}

export interface CheckoutRequest {
  tierId: number;
  billingCycle: 'monthly' | 'annual';
  discountCode?: string;
  successUrl: string;
  cancelUrl: string;
}

export interface CheckoutResponse {
  success: boolean;
  data: {
    sessionId: string;
    checkoutUrl: string;
  };
}

export interface SubscriptionTiersResponse {
  success: boolean;
  data: SubscriptionTier[];
}

export interface SubscriptionResponse {
  success: boolean;
  data: {
    subscription: Subscription | null;
    alaCarteFeatures: any[];
  } | null;
  message?: string;
}

export interface UpgradePreviewResponse {
  success: boolean;
  data: UpgradePreview;
}

export interface UpgradeRequest {
  tierId: number;
  billingCycle?: 'monthly' | 'annual';
  applyImmediately?: boolean;
}

export interface UpgradeResponse {
  success: boolean;
  data: {
    subscription: Subscription;
    change: {
      from: string;
      to: string;
      effectiveDate: string;
    };
  };
  error?: string;
  message?: string;
}

export interface CancelRequest {
  cancelImmediately?: boolean;
  reason?: string;
}

export interface CancelResponse {
  success: boolean;
  data: {
    canceled: boolean;
    cancelImmediately: boolean;
    endsAt: string;
  };
  error?: string;
  message?: string;
}

// ============== Service ==============

class SubscriptionService {
  /**
   * Get available subscription tiers
   * GET /subscription/tiers (public endpoint)
   */
  async getSubscriptionTiers(): Promise<SubscriptionTiersResponse> {
    const response = await fetch(`${API_BASE_URL}/subscription/tiers`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    return await response.json();
  }

  /**
   * Get current subscription for the authenticated organization
   * GET /subscription
   */
  async getCurrentSubscription(): Promise<SubscriptionResponse> {
    const response = await fetch(`${API_BASE_URL}/subscription`, {
      method: 'GET',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });

    return await response.json();
  }

  /**
   * Create a Stripe Checkout session for new subscription
   * POST /subscription/checkout
   */
  async createCheckout(request: CheckoutRequest): Promise<CheckoutResponse> {
    const response = await fetch(`${API_BASE_URL}/subscription/checkout`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    return await response.json();
  }

  /**
   * Preview proration for subscription upgrade
   * POST /subscription/preview
   */
  async previewUpgrade(tierId: number, billingCycle?: 'monthly' | 'annual'): Promise<UpgradePreviewResponse> {
    const response = await fetch(`${API_BASE_URL}/subscription/preview`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ tierId, billingCycle }),
    });

    const result = await response.json();

    // Normalize snake_case to camelCase if needed
    if (result.success && result.data) {
      const raw = result.data as UpgradePreviewRaw;
      result.data = {
        proratedAmount: Number(raw.proratedAmount ?? raw.prorated_amount) || 0,
        newPrice: Number(raw.newPrice ?? raw.new_price) || 0,
        newTier: raw.newTier ?? raw.new_tier ?? '',
        billingCycle: raw.billingCycle ?? raw.billing_cycle ?? 'monthly',
        nextBillingDate: raw.nextBillingDate ?? raw.next_billing_date ?? '',
      };
    }

    return result;
  }

  /**
   * Upgrade subscription to a new tier
   * POST /subscription/upgrade
   */
  async upgradeSubscription(request: UpgradeRequest): Promise<UpgradeResponse> {
    const response = await fetch(`${API_BASE_URL}/subscription/upgrade`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    return await response.json();
  }

  /**
   * Cancel subscription
   * POST /subscription/cancel
   */
  async cancelSubscription(request: CancelRequest = {}): Promise<CancelResponse> {
    const response = await fetch(`${API_BASE_URL}/subscription/cancel`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(request),
    });

    return await response.json();
  }

  /**
   * Validate a discount code
   * POST /subscription/discount/validate
   */
  async validateDiscountCode(code: string): Promise<{
    success: boolean;
    data?: {
      valid: boolean;
      promotionCodeId: string;
      couponId: string;
      code: string;
      discountType: 'percentage' | 'fixed';
      discountValue: number;
      duration: string;
      expiresAt: string | null;
    };
    error?: string;
    message?: string;
  }> {
    const response = await fetch(`${API_BASE_URL}/subscription/discount/validate`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });

    return await response.json();
  }
}

export const subscriptionService = new SubscriptionService();
